import type { DigitalSetup, Outcome, Priority, Variant } from '#lib/steps/state';
import {
  DIGITAL_SETUP_WEIGHTS,
  OUTCOME_WEIGHTS,
  PRIORITY_WEIGHTS,
  type WeightedOption,
} from './weights';

type Factor = { score: number; text: string };

const toFactors = <T extends string>(
  selected: T[] | undefined,
  weights: Record<T, WeightedOption>,
): Factor[] =>
  (selected ?? []).flatMap((id) => {
    const { weight, reason } = weights[id];
    return reason ? [{ score: weight, text: reason }] : [];
  });

export const getReasonText = (
  outcomes: Outcome[] | undefined,
  digitalSetups: DigitalSetup[] | undefined,
  priority: Priority | undefined,
  recommendedVariant: Variant,
): string => {
  // Scores come straight from the shared weight table, so the surfaced reason
  // always agrees with the recommendation: pick the highest-weight factor on
  // the winning side.
  const factors: Factor[] = [
    ...toFactors(outcomes, OUTCOME_WEIGHTS),
    ...toFactors(digitalSetups, DIGITAL_SETUP_WEIGHTS),
    ...(priority ? toFactors([priority], PRIORITY_WEIGHTS) : []),
  ];

  const winner = factors
    .filter(({ score }) => (recommendedVariant === 'hosted' ? score > 0 : score < 0))
    .sort((left, right) => Math.abs(right.score) - Math.abs(left.score))[0];

  return winner?.text ?? 'based on your needs';
};
